import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Share2, Copy, LogOut, ShieldCheck, Camera, Ticket } from "lucide-react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, Modal, Uploader, Loading } from "@/components/atoms";
import { MemberBadges } from "@/lib/badges";

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [perks, setPerks] = useState(null);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [newName, setNewName] = useState(user?.username || user?.name || "");

  const load = async () => {
    try { const { data } = await api.get("/perks"); setPerks(data); } catch (e) { setPerks({ perks: [], referral_count: 0 }); }
  };
  useEffect(() => {
    load();
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveAvatar = async () => {
    try { await api.post("/profile/avatar", { picture: avatarUrl }); await refreshUser(); setAvatarOpen(false); toast.success("Picture updated"); }
    catch (e) { toast.error("Failed"); }
  };

  const saveName = async () => {
    try { await api.post("/profile/username", { username: newName }); await refreshUser(); setNameOpen(false); toast.success("Username updated"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const share = async () => {
    const code = perks?.referral_code;
    const msg = `Join me at Alpha Tattoo Club! Use my referral code ${code} when you sign up.`;
    if (navigator.share) { try { await navigator.share({ text: msg }); return; } catch (e) {} }
    navigator.clipboard?.writeText(msg);
    toast.success("Referral copied");
  };

  if (!user) return <Loading />;
  const isAdmin = user.role === "admin";

  return (
    <div className="p-5" data-testid="profile-page">
      <div className="atc-card p-5 flex flex-col items-center text-center relative">
        <button onClick={() => setAvatarOpen(true)} className="relative" data-testid="profile-avatar-btn">
          <Avatar src={user.picture} name={user.username || user.name} size={90} admin={isAdmin} />
          <span className="absolute bottom-0 right-0 bg-[#8B1E28] border-2 border-black rounded-full p-1"><Camera size={14} color="#F4F0E6" /></span>
        </button>
        <h1 className="font-display font-black text-2xl mt-3 leading-none">{user.username || user.name}</h1>
        <p className="font-mono2 text-xs text-[#554E40] mt-1">{user.email}</p>
        <span className={`mt-3 border-2 border-black rounded px-3 py-1 font-mono2 text-[10px] uppercase ${isAdmin ? "bg-[#121212] text-[#F4F0E6]" : user.membership_status === "approved" ? "bg-[#8B1E28] text-[#F4F0E6]" : "bg-[#EAE4D3]"}`} data-testid="profile-status">
          {isAdmin ? "Staff / Admin" : user.membership_status === "approved" ? "Full Member" : user.membership_status}
        </span>
        <button className="atc-btn btn-outline mt-4 h-9 text-xs px-4" onClick={() => { setNewName(user.username || user.name); setNameOpen(true); }} data-testid="profile-edit-name-btn">
          Change Username
        </button>
        <div className="mt-4 w-full border-t-2 border-black pt-4">
          <p className="eyebrow mb-2">Your Badges</p>
          <MemberBadges user={user} center />
        </div>
      </div>

      {/* Referral card */}
      {perks && (
        <div className="atc-card p-5 mt-4 bg-[#121212] text-[#F4F0E6]" data-testid="referral-card">
          <div className="flex items-center justify-between">
            <span className="font-mono2 text-[10px] uppercase tracking-widest text-[#C7A656]">Referral Code</span>
            <ShieldCheck size={18} color="#C7A656" />
          </div>
          <p className="font-mono2 font-bold text-2xl tracking-wider mt-1" data-testid="referral-code">{perks.referral_code}</p>
          <p className="text-xs opacity-80 mt-1">{perks.referral_count} approved referral{perks.referral_count === 1 ? "" : "s"}</p>
          <div className="flex gap-2 mt-4">
            <button className="atc-btn btn-gold flex-1 h-10 text-xs" onClick={share} data-testid="referral-share-btn"><Share2 size={14} /> Share</button>
            <button className="atc-btn btn-gold h-10 px-4 text-xs" onClick={() => { navigator.clipboard?.writeText(perks.referral_code); toast.success("Code copied"); }} data-testid="referral-copy-btn"><Copy size={14} /></button>
          </div>
        </div>
      )}

      {/* Perks */}
      <h2 className="font-display font-bold text-xl uppercase mt-6 mb-3">My Perks</h2>
      {!perks ? <Loading /> : perks.perks.length === 0 ? (
        <p className="text-sm text-[#554E40]">No active perks yet. Get approved and invite friends to earn rewards.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {perks.perks.map((p) => (
            <div key={p.id} className="atc-card p-4 flex items-center gap-3" data-testid={`perk-${p.id}`}>
              <div className="w-11 h-11 rounded-full border-2 border-black bg-[#C7A656] flex items-center justify-center shrink-0"><Ticket size={20} /></div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold leading-tight">{p.title}</p>
                <p className="font-mono2 text-xs text-[#8B1E28] mt-1">{p.code}</p>
                {p.expiry && <p className="font-mono2 text-[10px] text-[#554E40]">Expires {new Date(p.expiry).toLocaleDateString()}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {perks?.redeemed?.length > 0 && (
        <>
          <h2 className="font-display font-bold text-xl uppercase mt-6 mb-3">Used Perks</h2>
          <div className="flex flex-col gap-3">
            {perks.redeemed.map((p) => (
              <div key={p.id} className="atc-card p-4 flex items-center gap-3 opacity-60" data-testid={`used-perk-${p.id}`}>
                <div className="w-11 h-11 rounded-full border-2 border-black bg-[#DCD4BC] flex items-center justify-center shrink-0"><Ticket size={20} /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold leading-tight line-through">{p.title}</p>
                  <p className="font-mono2 text-xs text-[#554E40] mt-1">{p.code} · redeemed {p.redeemed_at ? new Date(p.redeemed_at).toLocaleDateString() : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="flex flex-col gap-2 mt-6">
        {isAdmin && <button className="atc-btn btn-ink" onClick={() => navigate("/admin")} data-testid="profile-admin-btn"><ShieldCheck size={16} /> Admin Console</button>}
        <button className="atc-btn btn-primary" onClick={() => { logout(); navigate("/auth"); }} data-testid="profile-logout-btn"><LogOut size={16} /> Log Out</button>
      </div>

      <Modal open={avatarOpen} onClose={() => setAvatarOpen(false)} title="Change Picture" testid="avatar-modal">
        <div className="flex flex-col gap-3">
          {avatarUrl && <img src={fileUrl(avatarUrl)} alt="preview" className="w-24 h-24 object-cover rounded-full border-2 border-black mx-auto" />}
          <input className="atc-input" placeholder="Image URL" value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)} data-testid="avatar-url" />
          <Uploader onUploaded={(url) => setAvatarUrl(url)} label="Or upload" testid="avatar-upload" />
          <button className="atc-btn btn-primary" onClick={saveAvatar} disabled={!avatarUrl} data-testid="avatar-save-btn">Save</button>
        </div>
      </Modal>

      <Modal open={nameOpen} onClose={() => setNameOpen(false)} title="Change Username" testid="username-modal">
        <div className="flex flex-col gap-3">
          <p className="text-xs text-[#554E40]">You can change your username once every 90 days.</p>
          <input className="atc-input" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="username-input" />
          <button className="atc-btn btn-primary" onClick={saveName} data-testid="username-save-btn">Save Username</button>
        </div>
      </Modal>
    </div>
  );
}
